import Router from 'next/router';
import { Component, createRef } from 'react';
import { Subscription } from 'rxjs';
import { container, lazyInject } from '../../../context/module';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';
import { ApiConfig } from '../../../services/api-config';
import { LightHttp } from '../../../services/light-http.service';
import styles from './feature-search-box.component.module.scss';

interface SearchResult {
    slug: string;
    url: string;
    title: string;
    category: string;
}

interface SearchResponse {
    results: SearchResult[];
    query: string;
    count: number;
}

interface FeatureSearchBoxState {
    query: string;
    results: SearchResult[];
    isSearching: boolean;
    activeIndex: number;
    showDropdown: boolean;
}

export class FeatureSearchBox extends Component<{}, FeatureSearchBoxState> {
    @lazyInject(LightHttp) private lightHttp: LightHttp;
    @lazyInject(ApiConfig) private apiConfig: ApiConfig;

    private searchTimeout: ReturnType<typeof setTimeout>;
    private searchSubscription: Subscription;
    private containerRef = createRef<HTMLDivElement>();
    private navigating = false;

    state: FeatureSearchBoxState = {
        query: '',
        results: [],
        isSearching: false,
        activeIndex: -1,
        showDropdown: false,
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();
    }

    handleClickOutside = (e: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(e.target as Node)) {
            this.setState({ showDropdown: false });
        }
    };

    onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        this.setState({ query, activeIndex: -1 });

        clearTimeout(this.searchTimeout);
        this.searchSubscription?.unsubscribe();

        if (query.length < 2) {
            this.setState({ results: [], isSearching: false, showDropdown: false });
            return;
        }

        this.setState({ isSearching: true, showDropdown: true });

        this.searchTimeout = setTimeout(() => {
            this.searchSubscription = this.lightHttp.get<SearchResponse>(
                this.apiConfig.getFeatureSearchEndpoint(query)
            ).subscribe({
                next: (response) => {
                    const results = response.results || [];
                    this.setState({
                        results,
                        isSearching: false,
                    });
                    // Demand mining (sweep P2): zero-result queries are the new-tool
                    // backlog; the 300ms debounce keeps this one event per settled query.
                    container.get(MetricsService).emit(MetricEventName.FEATURE_SEARCH, {
                        query: query.slice(0, 120),
                        resultCount: results.length,
                    });
                },
                error: () => {
                    this.setState({ isSearching: false });
                },
            });
        }, 300);
    };

    onKeyDown = (e: React.KeyboardEvent) => {
        const { results, activeIndex, showDropdown } = this.state;
        if (!showDropdown || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ activeIndex: Math.min(activeIndex + 1, results.length - 1) });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ activeIndex: Math.max(activeIndex - 1, 0) });
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            this.navigateTo(results[activeIndex].url);
        } else if (e.key === 'Escape') {
            this.setState({ showDropdown: false });
        }
    };

    navigateTo = (url: string) => {
        container.get(MetricsService).emit(MetricEventName.FEATURE_SEARCH_CLICK, {
            query: (this.state.query || '').slice(0, 120),
            url,
        });
        if (this.navigating) return;
        this.navigating = true;
        this.setState({ showDropdown: false });
        Router.push(url);
    };

    render() {
        const { query, results, isSearching, activeIndex, showDropdown } = this.state;

        return (
            <div ref={this.containerRef} className={styles['search-card']}>
                <div className={styles['search-input-wrapper']}>
                    <span className={styles['search-icon']}>🔍</span>
                    <input
                        type="text"
                        className={styles['search-input']}
                        placeholder='Search for a tool... (e.g. "mp4 to mp3", "compress pdf")'
                        value={query}
                        onChange={this.onSearchInput}
                        onKeyDown={this.onKeyDown}
                        onFocus={() => { if (results.length > 0) this.setState({ showDropdown: true }); }}
                    />
                    {isSearching && <div className={styles.spinner} />}
                </div>

                {showDropdown && (
                    <div className={styles['results-dropdown']}>
                        {results.length > 0 ? (
                            results.map((result, i) => (
                                <div
                                    key={result.slug}
                                    className={`${styles['result-item']} ${i === activeIndex ? styles.active : ''}`}
                                    onClick={() => this.navigateTo(result.url)}
                                    onMouseEnter={() => this.setState({ activeIndex: i })}
                                >
                                    <div className={styles['result-info']}>
                                        <span className={styles['result-title']}>{result.title}</span>
                                        <span className={`${styles['category-badge']} ${styles[`category-${result.category}`] || ''}`}>
                                            {result.category}
                                        </span>
                                    </div>
                                    <button
                                        className={styles['go-btn']}
                                        onClick={(e) => { e.stopPropagation(); this.navigateTo(result.url); }}
                                    >
                                        Go →
                                    </button>
                                </div>
                            ))
                        ) : (
                            !isSearching && query.length >= 2 && (
                                <div className={styles['empty-state']}>
                                    No tools found for &ldquo;{query}&rdquo;. Try a different keyword or browse by category below.
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
        );
    }
}
